import {
    FC,
    HTMLAttributes,
    ReactNode,
    useCallback,
    useId,
    useState,
} from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { Heading } from "../Heading";
import { Text } from "../Text";
import styles from "./FilteringOptionsPanel.module.scss";

export type FilteringOptionsSection = {
    key: string;
    title: string;
    content: ReactNode;
};

export type FilteringOptionsPanelProps = HTMLAttributes<HTMLElement> & {
    title?: string;
    sections: FilteringOptionsSection[];
    /**
     * When true, the title toggles the sections. Collapse axis is CSS:
     * ≤1023px vertical (up), ≥1024px horizontal (left) via page chrome.
     */
    collapsible?: boolean;
    /** Controlled collapsed state. */
    collapsed?: boolean;
    /** Uncontrolled initial collapsed state. Default false. */
    defaultCollapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
};

const ChevronIcon = () => <Icon icon="chevron-down" />;

/**
 * Presentational “Filtering options” sidebar panel shared by Orders and
 * Order History. Layout chrome (sticky, borders, collapse rail) belongs
 * to page wrappers.
 */
export const FilteringOptionsPanel: FC<FilteringOptionsPanelProps> = ({
    title = "Filtering options",
    sections,
    className,
    collapsible = false,
    collapsed: collapsedProp,
    defaultCollapsed = false,
    onCollapsedChange,
    ...rest
}) => {
    const titleId = useId();
    const isControlled = collapsedProp !== undefined;
    const [uncontrolledCollapsed, setUncontrolledCollapsed] =
        useState(defaultCollapsed);
    const collapsed = isControlled ? collapsedProp : uncontrolledCollapsed;

    const setCollapsed = useCallback(
        (next: boolean) => {
            if (!isControlled) setUncontrolledCollapsed(next);
            onCollapsedChange?.(next);
        },
        [isControlled, onCollapsedChange],
    );

    const toggle = () => setCollapsed(!collapsed);

    return (
        <aside
            className={cx(styles.root, className)}
            data-collapsed={collapsible && collapsed ? "true" : undefined}
            data-collapsible={collapsible || undefined}
            data-ui="filtering-options-panel"
            {...rest}
        >
            {collapsible ? (
                <button
                    aria-controls={titleId}
                    aria-expanded={!collapsed}
                    className={styles.titleToggle}
                    onClick={toggle}
                    type="button"
                >
                    <Heading className={styles.title} level={6}>
                        {title}
                    </Heading>
                    <span aria-hidden className={styles.chevron}>
                        <ChevronIcon />
                    </span>
                </button>
            ) : (
                <Heading className={styles.title} level={6}>
                    {title}
                </Heading>
            )}
            <div
                aria-hidden={collapsible && collapsed ? true : undefined}
                className={styles.container}
                data-ui="filtering-options-container"
                id={collapsible ? titleId : undefined}
            >
                {sections.map(section => (
                    <div className={styles.section} key={section.key}>
                        <Text
                            className={styles.sectionLabel}
                            variant="label-md"
                        >
                            {section.title}
                        </Text>
                        {section.content}
                    </div>
                ))}
            </div>
        </aside>
    );
};

FilteringOptionsPanel.displayName = "FilteringOptionsPanel";
